// React
import { MutableRefObject, useEffect, useState } from "react";
import clsx from "clsx";

// MUI
import { Box, Grid, Icon, Stack, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";

// Third Party
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Context
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";

// Helpers
import { accountFormat } from "@Common/helpers/number";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import SalesLogAccumulativeChart from "@DataPages/components/SalesOperations/Tabs/SalesLog/SalesLogAccumulativeChart";
import SpeedometerChart from "@DataPages/components/SalesOperations/Tabs/SalesLog/SpeedometerChart";

// Assets
import AlertWithIcon from "@Layouts/components/MessagesAlert/AlertWithIcon";
import yellowIndicator from "@/assets/icons/dataPages/salesOperations/salesLog/YellowIndicator.svg";
import grayIndicator from "@/assets/icons/dataPages/salesOperations/salesLog/GrayIndicator.svg";
import empty from "@/assets/images/commons/empty.png";

// Types
import { GridColDef } from "@mui/x-data-grid-pro";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { IDynamicPVRInfo, IPVRMaxGraphValues, ISalesAnalyticsItem } from "@DataPages/types/salesOperations/salesLog/model";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";

type Trade = {
	id: string;
	trades: number;
	held?: number;
	pace?: number;
	heldPace?: number;
	perTrade: number;
};

type SalesLogAnalyticsProps = {
	isLoading: boolean;
	lastActiveCommissionCycle: ICommissionCycle;
	selectedCommissionCycle: ICommissionCycle;
	maxPvrValues: IPVRMaxGraphValues;
	data: ISalesAnalyticsItem[];
	dynamicPVRInfo: IDynamicPVRInfo;
	tabs: string[];
	selectedTab: string;
};

/******************************************** HELPERS *****************************************************/
const getPVRIndex = (ccCommissionCycle: ICommissionCycle, lastActiveCommissionCycle: ICommissionCycle) => {
	const isCommissionCycleActive =
		moment(ccCommissionCycle?.endDate)
			.utc()
			.isBetween(lastActiveCommissionCycle.startDate, lastActiveCommissionCycle.endDate, null, "[]") &&
		moment()
			.utc()
			.isBefore(ccCommissionCycle?.endDate);
	return isCommissionCycleActive
		? moment().utc().date() - 1
		: moment(ccCommissionCycle?.startDate)
				.utc()
				.daysInMonth() - 1;
};

const getGaugeValues = (tabIndex: string) => {
	switch (tabIndex) {
		case "F&I":
			return {
				original: "backPVR",
				projection: "backPVR",
			};
		case "Combined":
			return {
				original: "totalPVR",
				projection: "totalPVR",
			};
		default:
			return {
				original: "frontPVR",
				projection: "frontPVR",
			};
	}
};

function getGaugeChartMaxValue(tabIndex: string, maxPvrValues: IPVRMaxGraphValues = {} as IPVRMaxGraphValues) {
	switch (tabIndex) {
		case "F&I":
			return maxPvrValues?.maxFinancePVR;
		case "Combined":
			return maxPvrValues?.maxTotalPVR;
		case "Pre-Owned Vehicles":
			return maxPvrValues?.maxPreOwnedVehiclesPVR;
		default:
			return maxPvrValues?.maxNewVehiclesPVR;
	}
}

// Constants
const HEADER_CLASS_DEFINITION = "table-small-header payroll-preview-table-header show-as-column pink-variant";

export default function SalesLogAnalytics(props: Readonly<SalesLogAnalyticsProps>) {
	const { setTabSelected } = useSalesLogChart();

	// States
	const [speedometerValue, setSpeedometerValue] = useState(0);
	const [isHoveringChart, setIsHoveringChart] = useState(false);

	// Chart Elements
	const [activeDay, setActiveDay] = useState(moment(props.selectedCommissionCycle?.endDate).daysInMonth());

	// Widgets values
	const tabSelected = props.tabs[props.tabs.indexOf(props.selectedTab)];
	const data: ISalesAnalyticsItem = props.data?.[getPVRIndex(props.selectedCommissionCycle, props.lastActiveCommissionCycle)];
	const gaugeChartMaxValue = getGaugeChartMaxValue(tabSelected, props.maxPvrValues);
	const index = tabSelected;
	const waterFallData = props.data || [];

	const tables: { columns: GridColDef[]; apiRef: MutableRefObject<GridApiPremium>; rows: Trade[]; id: string }[] = [
		{
			columns: [
				{
					field: "_id",
					headerName: "ID",
					width: 10,
					filterable: false,
				},
				{
					field: "trades",
					headerName: "Trades",
					headerClassName: HEADER_CLASS_DEFINITION,
					cellClassName: "table-small-cell no-focus",
					flex: 1,
					sortable: false,
					renderCell: ({ value }) => parseInt(value, 10).toFixed(0),
				},
				{
					field: "held",
					headerName: "$ Held",
					flex: 1,
					sortable: false,
					align: "right",
					headerClassName: HEADER_CLASS_DEFINITION,
					cellClassName: (params) => {
						const isNegative = Math.sign(params.value) === -1;
						return clsx({
							"table-small-cell no-focus cell-with-dollar-symbol": true,
							"simple-negative-value": isNegative,
						});
					},
					renderCell: ({ value }) => {
						const isNegative = Math.sign(value) === -1;
						const showValue = accountFormat(Math.abs(value));
						if (isNegative) {
							return `(${showValue})`;
						}
						return showValue;
					},
				},
				{
					field: "perTrade",
					headerName: "Per Trade",
					flex: 1,
					sortable: false,
					align: "right",
					headerClassName: HEADER_CLASS_DEFINITION,
					cellClassName: (params) => {
						const isNegative = Math.sign(params.value) === -1;
						return clsx({
							"table-small-cell no-focus cell-with-dollar-symbol": true,
							"simple-negative-value": isNegative,
						});
					},
					renderCell: ({ value }) => {
						const isNegative = Math.sign(value) === -1;
						const showValue = accountFormat(Math.abs(value));
						if (isNegative) {
							return `(${showValue})`;
						}
						return showValue;
					},
				},
			],
			rows: [
				{
					id: uuidv4(),
					trades: Number(data?.summary?.totalTrades),
					held: Number(data?.projection?.current?.heldGross),
					perTrade: Number(data?.projection?.current?.heldPVR),
				},
			],
			apiRef: useGridApiRef(),
			id: "trades-table",
		},
		{
			columns: [
				{
					field: "_id",
					headerName: "ID",
					width: 10,
					filterable: false,
				},
				{
					field: "trades",
					headerName: "Trades Pace",
					headerClassName: HEADER_CLASS_DEFINITION,
					cellClassName: "table-small-cell no-focus",
					flex: 1,
					sortable: false,
					renderCell: ({ value }) => parseInt(value, 10).toFixed(0),
				},
				{
					field: "heldPace",
					headerName: "Held Pace",
					flex: 1,
					sortable: false,
					align: "right",
					headerClassName: HEADER_CLASS_DEFINITION,
					cellClassName: (params) => {
						const isNegative = Math.sign(params.value) === -1;
						return clsx({
							"table-small-cell no-focus cell-with-dollar-symbol": true,
							"simple-negative-value": isNegative,
						});
					},
					renderCell: ({ value }) => {
						const isNegative = Math.sign(value) === -1;
						const showValue = accountFormat(Math.abs(value));
						if (isNegative) {
							return `(${showValue})`;
						}
						return showValue;
					},
				},
				{
					field: "perTrade",
					headerName: "Per Trade",
					flex: 1,
					sortable: false,
					align: "right",
					headerClassName: HEADER_CLASS_DEFINITION,
					cellClassName: (params) => {
						const isNegative = Math.sign(params.value) === -1;
						return clsx({
							"table-small-cell no-focus cell-with-dollar-symbol": true,
							"simple-negative-value": isNegative,
						});
					},
					renderCell: ({ value }) => {
						const isNegative = Math.sign(value) === -1;
						const showValue = accountFormat(Math.abs(value));
						if (isNegative) {
							return `(${showValue})`;
						}
						return showValue;
					},
				},
			],
			rows: [
				{
					id: uuidv4(),
					trades: Number(data?.summary?.tradePace),
					heldPace: Number(data?.projection?.projected?.tradeGrossPace),
					perTrade: Number(data?.projection?.projected?.heldPVR),
				},
			],
			apiRef: useGridApiRef(),
			id: "trades-table",
		},
	];

	useEffect(() => {
		if (!tabSelected) {
			setTabSelected(props.tabs[0]);
		}
	}, [props.tabs, tabSelected]);

	if (!data && !props.isLoading) {
		return (
			<EmptyBody
				image={empty}
				imageStyles={{ width: "150px" }}
				title={"No Sales Log Analytics"}
				bodyHeight={"200px"}
				sx={{
					boxShadow: "none !important",
					paddingTop: "150px !important",
					paddingBottom: "125px !important",
				}}
			/>
		);
	}

	return (
		<Grid container mt={2} alignSelf={"flex-start"}>
			<Grid item xs={3} className="!min-w-[200px] !min-h-[1px]">
				<DataSmartTable
					apiRef={tables[0].apiRef}
					columns={tables[0].columns}
					rows={tables[0].rows}
					headerHeight={35}
					rowHeight={25}
					disableColumnMenu
					disableRowSelectionOnClick
					disableMultipleRowSelection
					checkboxSelection={false}
					autoHeight
					columnVisibilityModel={{
						_id: false,
					}}
					slots={{
						noRowsOverlay: NoRowsOverlay,
					}}
					slotProps={{
						noRowsOverlay: {
							title: "No Information Data",
						},
					}}
					hideFooter
					customProps={{
						tableContainerStyle: { height: "100%", width: "100%", minWidth: "200px" },
						tableClassName: {
							"functional-table": true,
						},
						expandableRows: true,
						hide: false,
					}}
				/>
			</Grid>
			<Grid item xs={6} pb={1}>
				<Stack direction={"column"} alignItems="center">
					<Typography className="!font-montserrat !font-medium !text-2xl !text-[var(--text-color)] !leading-tight !ml-[-50px]">
						{isHoveringChart ? "Daily Performance" : "MTD Performance"}
					</Typography>
					<Box mt={-4}>
						<SpeedometerChart
							data={data?.projection}
							dynamicPVRInfo={props.dynamicPVRInfo}
							useValues={getGaugeValues(index)}
							maxAxis={gaugeChartMaxValue ?? 8}
							isHoveringChart={isHoveringChart}
							speedometerValue={speedometerValue}
						/>
					</Box>
					<Typography
						className="text-[#B3B3B3] !font-[PTSansProNarrow] !leading-[1.2] !text-[12px]"
						width={"300px"}
						ml={-8}
						align={"center"}
					>
						The Total Gross PVR is defined by calculating
						<br /> different parameters and behaviors throughout the month.
					</Typography>
					<Stack direction={"row"} alignSelf={"flex-start"} pl={25} spacing={2} pt={2}>
						<AlertWithIcon
							className={"waterfall-legend-span"}
							text={"Month Pace PVR"}
							icon={
								<Icon fontSize="small">
									<img src={yellowIndicator} className="pb-[20px]" alt="current-total-groos-pvr" />
								</Icon>
							}
							textStyle={{ marginLeft: "3px" }}
						/>
						<AlertWithIcon
							className={"waterfall-legend-span"}
							text={"MTD PVR"}
							icon={
								<Icon fontSize="small">
									<img src={grayIndicator} className="pb-[20px]" alt="month-pace-pvr" />
								</Icon>
							}
							textStyle={{ marginLeft: "3px" }}
						/>
					</Stack>
				</Stack>
			</Grid>
			<Grid item xs={3}>
				<DataSmartTable
					apiRef={tables[1].apiRef}
					columns={tables[1].columns}
					rows={tables[1].rows}
					headerHeight={35}
					rowHeight={25}
					disableColumnMenu
					disableRowSelectionOnClick
					disableMultipleRowSelection
					checkboxSelection={false}
					autoHeight
					columnVisibilityModel={{
						_id: false,
					}}
					slots={{
						noRowsOverlay: NoRowsOverlay,
					}}
					slotProps={{
						noRowsOverlay: {
							title: "No Information Data",
						},
					}}
					hideFooter
					customProps={{
						tableContainerStyle: { height: "100%", width: "100%", minWidth: "200px" },
						tableClassName: {
							"functional-table": true,
						},
						expandableRows: true,
						hide: false,
					}}
				/>
			</Grid>

			<Grid item container ml={-2}>
				<Grid item pt={4} xs={12} lg={6}>
					<SalesLogAccumulativeChart
						id={"chart1"}
						data={waterFallData}
						chartTitle={"Sales Gross Progress"}
						selectedCommissionCycle={props?.selectedCommissionCycle}
						yAxisLabel={"Gross ($)"}
						activeDay={activeDay}
						setActiveDay={setActiveDay}
						dataProperty={"frontGross"}
						chartIndex={0}
						unitType={"finance"}
						lastActiveCommissionCycle={props.lastActiveCommissionCycle}
						isHoveringChart={isHoveringChart}
						// Callbacks
						setIsHoveringChart={setIsHoveringChart}
						setSpeedometerValue={setSpeedometerValue}
					/>
				</Grid>

				<Grid item pt={4} xs={12} md={6}>
					<SalesLogAccumulativeChart
						id={"chart1"}
						data={waterFallData}
						chartTitle={"Unit Volume Progress"}
						selectedCommissionCycle={props?.selectedCommissionCycle}
						lastActiveCommissionCycle={props.lastActiveCommissionCycle}
						yAxisLabel={"Units"}
						activeDay={activeDay}
						setActiveDay={setActiveDay}
						dataProperty={"deliveredToday"}
						chartIndex={1}
						unitType={"unit"}
						isHoveringChart={isHoveringChart}
						// Callbacks
						setIsHoveringChart={setIsHoveringChart}
						setSpeedometerValue={setSpeedometerValue}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}
